import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  signInWithEmailAndPassword,
  signInWithPopup,
  GoogleAuthProvider,
} from 'firebase/auth';
import {
  Mail,
  Lock,
  ArrowRight,
  AlertCircle,
  Check,
  Sparkles,
  ChevronLeft,
} from 'lucide-react';
import { auth } from '../firebase';
import { Logo } from '../components/Logo';
import { useAuth } from '../hooks/useAuth';
import { usePublicFlags } from '../hooks/usePublicFlags';
import { BASE_URL } from '../lib/api';

// ─── Bubble canvas animation ─────────────────────────────────────────────────

const MAX_BUBBLES = 40;

interface Bubble {
  id: number;
  x: number;
  y: number;
  r: number;
  vy: number;
  phase: number;
  amp: number;
  alpha: number;
  targetAlpha: number;
  state: 'fadein' | 'hold' | 'fadeout' | 'dead';
}

let nextBubbleId = 0;

function spawnBubble(canvasHeight: number, canvasWidth: number): Bubble {
  const r = 2 + Math.random() * 8;
  return {
    id: nextBubbleId++,
    x: Math.random() * canvasWidth,
    y: canvasHeight + r,
    r,
    vy: 0.6 + Math.random() * 1.1,
    phase: Math.random() * Math.PI * 2,
    amp: 8 + Math.random() * 24,
    alpha: 0,
    targetAlpha: 0.04 + Math.random() * 0.08,
    state: 'fadein',
  };
}

function BubbleCanvas() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const bubblesRef = useRef<Bubble[]>([]);
  const frameRef = useRef<number>(0);
  const lastSpawnRef = useRef<number>(0);

  const tick = useCallback((ts: number) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const W = canvas.width;
    const H = canvas.height;

    // Sporadic spawn: singles + occasional clusters
    if (ts - lastSpawnRef.current > 400 + Math.random() * 800) {
      lastSpawnRef.current = ts;
      const count = Math.random() < 0.25 ? 2 + Math.floor(Math.random() * 3) : 1;
      if (bubblesRef.current.length < MAX_BUBBLES) {
        for (let i = 0; i < count; i++) {
          if (bubblesRef.current.length < MAX_BUBBLES) {
            bubblesRef.current.push(spawnBubble(H, W));
          }
        }
      }
    }

    ctx.clearRect(0, 0, W, H);

    bubblesRef.current = bubblesRef.current.filter((b) => b.state !== 'dead');

    for (const b of bubblesRef.current) {
      b.y -= b.vy;
      b.x += Math.sin(b.y / 40 + b.phase) * 0.4;

      const progress = 1 - b.y / H;

      if (b.state === 'fadein') {
        b.alpha = Math.min(b.alpha + 0.005, b.targetAlpha);
        if (b.alpha >= b.targetAlpha) b.state = 'hold';
      } else if (b.state === 'hold') {
        if (progress > 0.75) b.state = 'fadeout';
      } else if (b.state === 'fadeout') {
        b.alpha = Math.max(b.alpha - 0.008, 0);
        if (b.alpha <= 0) { b.state = 'dead'; continue; }
      }

      if (b.y + b.r < 0) { b.state = 'dead'; continue; }

      ctx.beginPath();
      ctx.arc(b.x, b.y, b.r, 0, Math.PI * 2);
      ctx.fillStyle = `rgba(255,255,255,${b.alpha.toFixed(3)})`;
      ctx.fill();
    }

    frameRef.current = requestAnimationFrame(tick);
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const mo = window.matchMedia('(prefers-reduced-motion: reduce)');
    if (mo.matches) return;

    const resize = () => {
      canvas.width = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
    };
    resize();
    const ro = new ResizeObserver(resize);
    ro.observe(canvas);

    frameRef.current = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(frameRef.current);
      ro.disconnect();
    };
  }, [tick]);

  return (
    <canvas
      ref={canvasRef}
      className="absolute inset-0 w-full h-full pointer-events-none"
      aria-hidden="true"
    />
  );
}

// ─── Existing bubble data for standard mode ───────────────────────────────────

const BUBBLES = Array.from({ length: 10 }, (_, i) => ({
  id: i,
  size: 20 + (i % 5) * 16 + (i * 7) % 40,
  left: 5 + (i * 10) % 90,
  delay: (i * 2.3) % 12,
  duration: 15 + (i * 3) % 12,
  wobble: 3 + (i % 4),
}));

const TESTIMONIAL = {
  quote:
    "Documentation is all over the place — our Confluence is a nightmare. What I really need is a single view that tells me which projects are actually at risk, right now.",
  author: 'Senior Product Manager',
  avatar: 'https://i.pravatar.cc/150?img=11',
};

// ─── Firebase error formatting ────────────────────────────────────────────────

function formatFirebaseError(code: string): string {
  switch (code) {
    case 'auth/user-not-found':
      return 'No account found with this email address.';
    case 'auth/wrong-password':
    case 'auth/invalid-credential':
      return 'Incorrect email or password.';
    case 'auth/too-many-requests':
      return 'Too many failed attempts. Please try again later.';
    case 'auth/user-disabled':
      return 'This account has been disabled.';
    case 'auth/invalid-email':
      return 'Please enter a valid email address.';
    default:
      return 'An error occurred. Please try again.';
  }
}

// ─── Types ────────────────────────────────────────────────────────────────────

type Screen = 'login' | 'waitlist' | 'success';

const TEAM_SIZE_OPTIONS = ['Just me', '2 – 10', '11 – 50', '51 – 200', '200+'];

const ROLE_OPTIONS = [
  'Product Manager',
  'Engineering Lead / Manager',
  'Engineer / IC',
  'Designer',
  'Founder / Exec',
  'Operations / Chief of Staff',
  'Program / Project Manager',
  'Other',
];

// ─── Screen transition variants ───────────────────────────────────────────────

const screenVariants = {
  initial: { opacity: 0, x: 20 },
  animate: { opacity: 1, x: 0 },
  exit: { opacity: 0, x: -20 },
};

// ─── Login form (standard + beta mode) ───────────────────────────────────────

interface LoginFormProps {
  onSwitchToWaitlist: () => void;
  privateBeta: boolean;
}

function LoginForm({ onSwitchToWaitlist, privateBeta }: LoginFormProps) {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resetSent, setResetSent] = useState(false);
  const [showReset, setShowReset] = useState(false);
  const [resetEmail, setResetEmail] = useState('');

  const handleEmailSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      await signInWithEmailAndPassword(auth, email, password);
      navigate('/');
    } catch (err) {
      const code = (err as { code?: string }).code ?? '';
      setError(formatFirebaseError(code));
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setError(null);
    setLoading(true);
    try {
      const provider = new GoogleAuthProvider();
      await signInWithPopup(auth, provider);
      navigate('/');
    } catch (err) {
      const code = (err as { code?: string }).code ?? '';
      if (code !== 'auth/popup-closed-by-user') {
        setError(formatFirebaseError(code));
      }
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordReset = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      await fetch(`${BASE_URL}/auth/password-reset`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: resetEmail }),
      });
      setResetSent(true);
    } catch {
      // best-effort — always show success to avoid account enumeration
      setResetSent(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="w-full max-w-md">
      {!privateBeta && (
        <div className="flex justify-center mb-8">
          <Logo className="h-10 w-auto text-gray-900 dark:text-white" />
        </div>
      )}

      <div className={`text-center ${privateBeta ? 'mb-6' : 'mb-8'}`}>
        <h1 className="text-2xl font-bold font-display text-gray-900 dark:text-white">
          {showReset ? 'Reset Password' : 'Welcome back'}
        </h1>
        <p className="text-gray-500 dark:text-gray-400 text-meta mt-1">
          {showReset
            ? 'Enter your email to receive a reset link'
            : 'Navigate product waters with OctoPM'}
        </p>
      </div>

      {error && (
        <motion.div
          initial={{ opacity: 0, y: -8 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-center gap-2 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-900/30 rounded-xl mb-4 text-meta text-red-600 dark:text-red-400"
        >
          <AlertCircle className="h-4 w-4 flex-shrink-0" />
          {error}
        </motion.div>
      )}

      {resetSent ? (
        <div className="text-center space-y-4">
          <div className="p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-900/30 rounded-xl text-meta text-green-700 dark:text-green-400">
            Reset link sent to <strong>{resetEmail}</strong>. Check your inbox.
          </div>
          <button
            onClick={() => { setShowReset(false); setResetSent(false); }}
            className="text-meta text-primary hover:text-primary/80 transition-colors"
          >
            Back to sign in
          </button>
        </div>
      ) : showReset ? (
        <form onSubmit={handlePasswordReset} className="space-y-4">
          <div className="relative">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="email"
              value={resetEmail}
              onChange={(e) => setResetEmail(e.target.value)}
              placeholder="you@company.com"
              required
              className="w-full pl-10 pr-4 py-2.5 bg-gray-50 dark:bg-white/5 border border-gray-200 dark:border-white/10 rounded-xl text-gray-900 dark:text-white text-meta placeholder:text-gray-500 dark:placeholder:text-gray-400 focus:outline-none focus:border-primary/50 focus:ring-1 focus:ring-primary/30 transition-colors"
            />
          </div>
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            type="submit"
            disabled={loading}
            className="w-full py-2.5 bg-gray-800 dark:bg-primary text-white rounded-full font-medium text-meta hover:bg-gray-900 dark:hover:bg-primary/90 transition-colors disabled:opacity-50"
          >
            {loading ? 'Sending...' : 'Send Reset Link'}
          </motion.button>
          <button
            type="button"
            onClick={() => setShowReset(false)}
            className="w-full text-meta text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white transition-colors"
          >
            Back to sign in
          </button>
        </form>
      ) : (
        <>
          {/* Private beta waitlist banner */}
          {privateBeta && (
            <div className="mb-5 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800/40 rounded-xl flex items-start gap-3">
              <Sparkles className="h-4 w-4 text-blue-500 mt-0.5 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-micro text-blue-700 dark:text-blue-300 leading-relaxed">
                  {"We're in private beta."}
                </p>
              </div>
              <button
                type="button"
                onClick={onSwitchToWaitlist}
                className="text-micro font-medium text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-200 transition-colors whitespace-nowrap flex-shrink-0"
              >
                Join the waitlist →
              </button>
            </div>
          )}

          {/* Google */}
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={handleGoogleSignIn}
            disabled={loading}
            className="w-full py-2.5 bg-gray-50 dark:bg-white/5 hover:bg-gray-100 dark:hover:bg-white/10 border border-gray-200 dark:border-white/10 text-gray-700 dark:text-white rounded-lg font-medium text-meta transition-colors disabled:opacity-50 flex items-center justify-center gap-2 mb-5"
          >
            <img src="/integrations/google.svg" alt="" className="h-4 w-4 object-contain" />
            Continue with Google
          </motion.button>

          <div className="flex items-center gap-3 mb-5">
            <div className="flex-1 h-px bg-gray-200 dark:bg-white/10" />
            <span className="text-micro text-gray-400">or</span>
            <div className="flex-1 h-px bg-gray-200 dark:bg-white/10" />
          </div>

          {/* Email form */}
          <form onSubmit={handleEmailSignIn} className="space-y-4">
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
                required
                autoComplete="email"
                className="w-full pl-10 pr-4 py-2.5 bg-gray-50 dark:bg-white/5 border border-gray-200 dark:border-white/10 rounded-xl text-gray-900 dark:text-white text-meta placeholder:text-gray-500 dark:placeholder:text-gray-400 focus:outline-none focus:border-primary/50 focus:ring-1 focus:ring-primary/30 transition-colors"
              />
            </div>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Password"
                required
                autoComplete="current-password"
                className="w-full pl-10 pr-4 py-2.5 bg-gray-50 dark:bg-white/5 border border-gray-200 dark:border-white/10 rounded-xl text-gray-900 dark:text-white text-meta placeholder:text-gray-500 dark:placeholder:text-gray-400 focus:outline-none focus:border-primary/50 focus:ring-1 focus:ring-primary/30 transition-colors"
              />
            </div>
            <div className="flex justify-end">
              <button
                type="button"
                onClick={() => setShowReset(true)}
                className="text-micro text-gray-400 hover:text-primary transition-colors"
              >
                Forgot password?
              </button>
            </div>
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              type="submit"
              disabled={loading}
              className="w-full py-2.5 bg-gray-800 dark:bg-primary text-white rounded-full font-medium text-meta hover:bg-gray-900 dark:hover:bg-primary/90 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {loading ? 'Signing in...' : (
                <>
                  Sign in
                  <ArrowRight className="h-4 w-4" />
                </>
              )}
            </motion.button>
          </form>

          {privateBeta && (
            <p className="text-center text-micro text-gray-400 mt-4">
              New here?{' '}
              <button
                type="button"
                onClick={onSwitchToWaitlist}
                className="text-primary hover:text-primary/80 transition-colors"
              >
                Request early access
              </button>
            </p>
          )}
        </>
      )}

      <p className="text-center text-micro text-gray-400 mt-6">
        Need help?{' '}
        <a href="mailto:support@octo.pm" className="text-primary hover:text-primary/80 transition-colors">
          Contact support
        </a>
      </p>
    </div>
  );
}

// ─── Waitlist form ────────────────────────────────────────────────────────────

interface WaitlistResult {
  position: number;
}

interface WaitlistFormProps {
  onBack: () => void;
  onSuccess: (result: WaitlistResult, name: string, email: string) => void;
}

function WaitlistForm({ onBack, onSuccess }: WaitlistFormProps) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [company, setCompany] = useState('');
  const [teamSize, setTeamSize] = useState('');
  const [role, setRole] = useState('');
  const [why, setWhy] = useState('');
  const [techStack, setTechStack] = useState('');
  const [emailUpdates, setEmailUpdates] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const WHY_MAX = 500;

  const isValid =
    name.trim() !== '' &&
    email.trim() !== '' &&
    company.trim() !== '' &&
    teamSize !== '' &&
    role !== '' &&
    why.trim() !== '' &&
    techStack.trim() !== '';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isValid) return;
    setError(null);
    setLoading(true);
    try {
      const res = await fetch(`${BASE_URL}/waitlist`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: name.trim(),
          email: email.trim(),
          company: company.trim(),
          team_size: teamSize,
          role,
          why: why.trim(),
          tech_stack: techStack.trim(),
          email_updates: emailUpdates,
        }),
      });
      if (!res.ok) {
        let msg = `Submission failed (${res.status})`;
        try {
          const body = await res.json() as { detail?: string };
          if (body.detail) msg = body.detail;
        } catch { /* ignore */ }
        throw new Error(msg);
      }
      const json = await res.json();
      const data = (json.data ?? json) as WaitlistResult;
      onSuccess(data, name.trim(), email.trim());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    'w-full px-3 py-2.5 bg-gray-50 dark:bg-white/5 border border-gray-200 dark:border-white/10 rounded-xl text-gray-900 dark:text-white text-meta placeholder:text-gray-500 dark:placeholder:text-gray-400 focus:outline-none focus:border-primary/50 focus:ring-1 focus:ring-primary/30 transition-colors';

  const sectionHeaderCls =
    'text-micro font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500 mb-3 mt-6 first:mt-0';

  return (
    <div className="w-full max-w-lg">
      <button
        type="button"
        onClick={onBack}
        className="flex items-center gap-1 text-meta text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors mb-6"
      >
        <ChevronLeft className="h-4 w-4" />
        Back to sign in
      </button>

      <div className="mb-1">
        <span className="text-micro font-semibold uppercase tracking-wider text-primary">
          Private beta
        </span>
      </div>
      <h1 className="text-2xl font-bold font-display text-gray-900 dark:text-white mb-1">
        Request early access
      </h1>
      <p className="text-meta text-gray-500 dark:text-gray-400 mb-5">
        The more you tell us, the faster we can fast-track you.
      </p>

      {error && (
        <motion.div
          initial={{ opacity: 0, y: -8 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-center gap-2 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-900/30 rounded-xl mb-4 text-meta text-red-600 dark:text-red-400"
        >
          <AlertCircle className="h-4 w-4 flex-shrink-0" />
          {error}
        </motion.div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        <p className={sectionHeaderCls}>About you</p>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-meta font-medium text-gray-600 dark:text-gray-400 mb-1">
              Full name <span className="text-red-400">*</span>
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Jane Smith"
              required
              className={inputCls}
            />
          </div>
          <div>
            <label className="block text-meta font-medium text-gray-600 dark:text-gray-400 mb-1">
              Work email <span className="text-red-400">*</span>
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="jane@company.com"
              required
              className={inputCls}
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-meta font-medium text-gray-600 dark:text-gray-400 mb-1">
              Company <span className="text-red-400">*</span>
            </label>
            <input
              type="text"
              value={company}
              onChange={(e) => setCompany(e.target.value)}
              placeholder="Acme Corp"
              required
              className={inputCls}
            />
          </div>
          <div>
            <label className="block text-meta font-medium text-gray-600 dark:text-gray-400 mb-1">
              Team size <span className="text-red-400">*</span>
            </label>
            <select
              value={teamSize}
              onChange={(e) => setTeamSize(e.target.value)}
              required
              className={inputCls}
            >
              <option value="">Select size</option>
              {TEAM_SIZE_OPTIONS.map((o) => (
                <option key={o} value={o}>{o}</option>
              ))}
            </select>
          </div>
        </div>

        <div>
          <label className="block text-meta font-medium text-gray-600 dark:text-gray-400 mb-1">
            Role <span className="text-red-400">*</span>
          </label>
          <select
            value={role}
            onChange={(e) => setRole(e.target.value)}
            required
            className={inputCls}
          >
            <option value="">Select your role</option>
            {ROLE_OPTIONS.map((o) => (
              <option key={o} value={o}>{o}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-meta font-medium text-gray-600 dark:text-gray-400 mb-1">
            Why do you want to try the OctoPM beta?{' '}
            <span className="text-red-400">*</span>
          </label>
          <div className="relative">
            <textarea
              value={why}
              onChange={(e) => setWhy(e.target.value.slice(0, WHY_MAX))}
              placeholder="Tell us about your current workflow challenges..."
              required
              rows={3}
              className={`${inputCls} resize-none`}
            />
            <span className={`absolute bottom-2 right-3 text-micro ${why.length >= WHY_MAX ? 'text-red-400' : 'text-gray-400'}`}>
              {why.length}/{WHY_MAX}
            </span>
          </div>
        </div>

        <div>
          <label className="block text-meta font-medium text-gray-600 dark:text-gray-400 mb-1">
            Current tech stack{' '}
            <span className="text-red-400">*</span>
          </label>
          <input
            type="text"
            value={techStack}
            onChange={(e) => setTechStack(e.target.value)}
            placeholder="e.g. Jira, GitHub, Slack, Notion"
            required
            className={inputCls}
          />
        </div>

        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={emailUpdates}
            onChange={(e) => setEmailUpdates(e.target.checked)}
            className="rounded border-gray-300 dark:border-white/20 text-primary focus:ring-primary"
          />
          <span className="text-micro text-gray-500 dark:text-gray-400">
            Keep me in the loop with product updates and beta news
          </span>
        </label>

        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          type="submit"
          disabled={loading || !isValid}
          className="w-full py-2.5 bg-gray-800 dark:bg-primary text-white rounded-full font-medium text-meta hover:bg-gray-900 dark:hover:bg-primary/90 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
        >
          {loading ? 'Reserving your spot...' : (
            <>
              Reserve my spot
              <ArrowRight className="h-4 w-4" />
            </>
          )}
        </motion.button>
      </form>

      <p className="text-center text-micro text-gray-400 mt-4">
        Already have an invite code?{' '}
        <button
          type="button"
          onClick={onBack}
          className="text-primary hover:text-primary/80 transition-colors"
        >
          Sign in instead
        </button>
      </p>
    </div>
  );
}

// ─── Success screen ───────────────────────────────────────────────────────────

interface SuccessScreenProps {
  firstName: string;
  email: string;
  onBack: () => void;
}

function SuccessScreen({ firstName, email, onBack }: SuccessScreenProps) {
  return (
    <div className="w-full max-w-md text-center">
      <div className="flex justify-center mb-4">
        <div className="h-14 w-14 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
          <Check className="h-7 w-7 text-green-600 dark:text-green-400" />
        </div>
      </div>

      <div className="mb-1">
        <span className="text-micro font-semibold uppercase tracking-wider text-primary">
          {"You're on the list"}
        </span>
      </div>
      <h1 className="text-2xl font-bold font-display text-gray-900 dark:text-white mb-2">
        Welcome aboard, {firstName} 🐙
      </h1>
      <p className="text-meta text-gray-500 dark:text-gray-400 mb-6">
        {"Thanks for your interest, "}{firstName}
        {". We review new teams regularly and will be in touch at "}
        <strong className="text-gray-700 dark:text-gray-300">{email}</strong>
        {" when there's a spot for you."}
      </p>

      <motion.button
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
        type="button"
        onClick={onBack}
        className="w-full py-2.5 bg-gray-100 dark:bg-white/10 text-gray-700 dark:text-white rounded-full font-medium text-meta hover:bg-gray-200 dark:hover:bg-white/15 transition-colors"
      >
        Back to sign in
      </motion.button>
    </div>
  );
}

// ─── Main Login component ─────────────────────────────────────────────────────

export function Login() {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const { privateBeta, isLoading: flagsLoading } = usePublicFlags();

  const [screen, setScreen] = useState<Screen>('login');
  const [waitlistResult, setWaitlistResult] = useState<WaitlistResult | null>(null);
  const [successName, setSuccessName] = useState('');
  const [successEmail, setSuccessEmail] = useState('');

  useEffect(() => {
    if (!authLoading && user) {
      navigate('/', { replace: true });
    }
  }, [authLoading, user, navigate]);

  if (authLoading || user) return null;

  // While flags are loading, show a minimal skeleton so we don't flash wrong content
  if (flagsLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white dark:bg-bg-deep">
        <div className="w-8 h-8 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
      </div>
    );
  }

  const handleWaitlistSuccess = (result: WaitlistResult, name: string, email: string) => {
    setWaitlistResult(result);
    setSuccessName(name.split(' ')[0]);
    setSuccessEmail(email);
    setScreen('success');
  };

  // ── Standard mode (no private beta) ────────────────────────────────────────
  if (!privateBeta) {
    return (
      <div className="min-h-screen flex w-full bg-white dark:bg-bg-deep text-gray-900 dark:text-white">
        {/* Left — Login form */}
        <div className="w-full lg:w-1/2 flex items-center justify-center p-8 lg:p-16">
          <LoginForm onSwitchToWaitlist={() => setScreen('waitlist')} privateBeta={false} />
        </div>

        {/* Right — Branding panel (lg+) */}
        <div className="hidden lg:flex w-1/2 bg-primary relative overflow-hidden items-center justify-center p-16">
          <div className="absolute inset-0 bg-[url('/background.svg')] opacity-20 bg-cover bg-center mix-blend-overlay pointer-events-none" />

          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            {BUBBLES.map((b) => (
              <div
                key={b.id}
                className="bubble"
                style={{
                  width: b.size,
                  height: b.size,
                  left: `${b.left}%`,
                  bottom: `-${b.size}px`,
                  background: 'rgba(255, 255, 255, 0.25)',
                  '--duration': `${b.duration}s`,
                  '--wobble': `${b.wobble}s`,
                  animationDelay: `${b.delay}s`,
                } as React.CSSProperties}
              />
            ))}
          </div>

          <div className="relative z-10 w-full max-w-sm">
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              transition={{ delay: 0.25, duration: 0.5, ease: 'easeOut' }}
              className="bg-white rounded-2xl p-8 shadow-2xl relative"
            >
              <div className="absolute -top-10 left-1/2 -translate-x-1/2 w-20 h-20 rounded-full border-4 border-white overflow-hidden shadow-lg">
                <img
                  src={TESTIMONIAL.avatar}
                  alt={TESTIMONIAL.author}
                  className="w-full h-full object-cover"
                />
              </div>

              <div className="mt-10 text-center space-y-4">
                <p className="text-gray-700 text-meta leading-relaxed">
                  "{TESTIMONIAL.quote}"
                </p>
                <p className="text-gray-400 text-micro font-medium">{TESTIMONIAL.author}</p>
              </div>
            </motion.div>

            <p className="text-center text-white/60 text-micro mt-6">
              Trusted by product teams who need clarity, not more noise.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // ── Private beta mode ───────────────────────────────────────────────────────
  return (
    <div className="min-h-screen flex w-full bg-white dark:bg-bg-deep text-gray-900 dark:text-white">

      {/* Left — Brand panel */}
      <div className="hidden lg:flex w-1/2 bg-[#0060d1] relative overflow-hidden flex-col items-center justify-center p-16">
        <BubbleCanvas />

        <div className="relative z-10 w-full max-w-sm">
          {/* Logo */}
          <div className="mb-8">
            <Logo className="h-9 w-auto text-white" monochrome />
          </div>

          {/* Eyebrow */}
          <p className="text-blue-400 text-micro font-semibold uppercase tracking-widest mb-3">
            Private beta · Summer 2026
          </p>

          {/* Headline */}
          <h2 className="text-3xl font-bold font-display text-white leading-tight mb-6">
            Project management with eight arms, not eight tabs.
          </h2>

          {/* Subtitle */}
          <p className="text-white/60 text-meta leading-relaxed mb-8">
            OctoPM ingests signals from Slack, Jira, Linear, and Google Drive — then surfaces what actually needs your attention today. Risk evaluation, delivery confidence, and proactive actions, all in one place.
          </p>

          {/* Feature checklist */}
          <ul className="space-y-4 mb-12">
            {[
              'AI-powered risk evaluation across your portfolio',
              'Daily briefings with ready-to-execute actions',
              'Slack, Jira, Linear & Google Drive integration',
            ].map((feat) => (
              <li key={feat} className="flex items-center gap-3">
                <div className="h-5 w-5 rounded-full bg-blue-500/20 border border-blue-500/40 flex items-center justify-center flex-shrink-0">
                  <Check className="h-4 w-4 text-blue-400" />
                </div>
                <span className="text-white/80 text-meta">{feat}</span>
              </li>
            ))}
          </ul>

          {/* Footer */}
          <p className="text-white/30 text-micro">
            © 2026 OctoPM, Inc. · All rights reserved
          </p>
        </div>
      </div>

      {/* Right — Screen state machine */}
      <div className="w-full lg:w-1/2 flex flex-col p-8 lg:p-16 overflow-y-auto">
        <div className="w-full flex justify-center py-4 my-auto">
          {/* Logo visible on mobile only (left panel is hidden) */}
          <div className="lg:hidden mb-8 flex justify-center">
            <Logo className="h-8 w-auto text-gray-900 dark:text-white" />
          </div>

          <AnimatePresence mode="wait">
            {screen === 'login' && (
              <motion.div
                key="login"
                variants={screenVariants}
                initial="initial"
                animate="animate"
                exit="exit"
                transition={{ duration: 0.2 }}
                className="w-full flex justify-center"
              >
                <LoginForm
                  onSwitchToWaitlist={() => setScreen('waitlist')}
                  privateBeta={true}
                />
              </motion.div>
            )}

            {screen === 'waitlist' && (
              <motion.div
                key="waitlist"
                variants={screenVariants}
                initial="initial"
                animate="animate"
                exit="exit"
                transition={{ duration: 0.2 }}
                className="w-full flex justify-center"
              >
                <WaitlistForm
                  onBack={() => setScreen('login')}
                  onSuccess={handleWaitlistSuccess}
                />
              </motion.div>
            )}

            {screen === 'success' && waitlistResult && (
              <motion.div
                key="success"
                variants={screenVariants}
                initial="initial"
                animate="animate"
                exit="exit"
                transition={{ duration: 0.2 }}
                className="w-full flex justify-center"
              >
                <SuccessScreen
                  firstName={successName}
                  email={successEmail}
                  onBack={() => setScreen('login')}
                />
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

    </div>
  );
}
